import axios from 'axios';
import { withServerMessage } from '../../utils/apiErrorMessage';

// Instance axios pour le Owner Portal
// Utilise un token séparé (owner-token) de l'auth admin
const ownerApi = axios.create({
  baseURL: process.env.REACT_APP_SERVER_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Flag pour éviter les redirections multiples simultanées
let isRedirecting = false;

// Intercepteur pour ajouter le token owner
ownerApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('owner-token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Intercepteur pour gérer les erreurs 401
ownerApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Log pour debug
      console.warn('🔐 Owner 401 Unauthorized:', {
        url: error.config?.url,
        message: error.response?.data?.message,
        hasToken: !!localStorage.getItem('owner-token')
      });

      // Éviter les redirections multiples et les boucles
      if (!isRedirecting && !window.location.pathname.includes('/owner/login')) {
        isRedirecting = true;
        localStorage.removeItem('owner-token');
        localStorage.removeItem('owner-data');
        // The login route is slugged (/:companySlug/owner/login) — derive the slug
        // from the current path; without it, fall back to root (DomainGuard routes it)
        const slugMatch = window.location.pathname.match(/^\/([^/]+)\/owner(\/|$)/);
        window.location.href = slugMatch ? `/${slugMatch[1]}/owner/login` : '/';
      }
    }
    return Promise.reject(error);
  }
);

// Last in the chain: errors carry the server's reason as `message` (see apiErrorMessage)
ownerApi.interceptors.response.use(undefined, withServerMessage);

export const ownerAPI = {
  // ============================================
  // PUBLIC (No Auth)
  // ============================================

  getCompanyBySlug: (slug) =>
    ownerApi.get(`/owner/company/${slug}`),

  // ============================================
  // AUTH
  // ============================================

  login: (email, password) =>
    ownerApi.post('/owner/login', { email, password }),

  acceptInvite: (token, password) =>
    ownerApi.post('/owner/accept-invite', { token, password }),

  forgotPassword: (email) =>
    ownerApi.post('/owner/forgot-password', { email }),

  resetPassword: (token, password) =>
    ownerApi.post('/owner/reset-password', { token, password }),

  getMe: () =>
    ownerApi.get('/owner/me'),

  updateProfile: (data) =>
    ownerApi.put('/owner/me', data),

  getDocuments: () =>
    ownerApi.get('/owner/documents'),

  // ============================================
  // DATA
  // ============================================

  getStats: (month, year, unitId = '') =>
    ownerApi.get('/owner/stats', { params: { month, year, ...(unitId && { unitId }) } }),

  getFeed: (month, year, unitId = '') =>
    ownerApi.get('/owner/feed', { params: { month, year, ...(unitId && { unitId }) } }),

  getCalendar: (month, year, unitId = '') =>
    ownerApi.get('/owner/calendar', { params: { month, year, ...(unitId && { unitId }) } }),

  getReservations: (month, year, unitId = '') =>
    ownerApi.get('/owner/reservations', { params: { month, year, ...(unitId && { unitId }) } }),

  getListings: (month, year, unitId = '') =>
    ownerApi.get('/owner/listings', { params: { month, year, ...(unitId && { unitId }) } }),

  getReleves: (year, unitId = '') =>
    ownerApi.get('/owner/releves', { params: { year, ...(unitId && { unitId }) } }),

  getReleveDetail: (payoutId, unitId = '') =>
    ownerApi.get(`/owner/releves/${payoutId}`, { params: { ...(unitId && { unitId }) } }),

  downloadPDF: (payoutId) =>
    ownerApi.get(`/owner/releves/${payoutId}/pdf`, { responseType: 'blob' }),

  getExpenseReceiptUrl: (expenseId, download = false) =>
    ownerApi.get(`/owner/expenses/${expenseId}/receipt-url`, { params: { download } }),

  // ============================================
  // EXPENSES (Read-only view)
  // ============================================

  getExpenses: (month, year) =>
    ownerApi.get('/owner/expenses', { params: { month, year } }),

  // ============================================
  // WEB PUSH (notifications navigateur / PWA)
  // ============================================

  getPushPublicKey: () =>
    ownerApi.get('/owner/push/public-key').then((r) => r.data),

  subscribePush: (subscription) =>
    ownerApi.post('/owner/push/subscribe', { subscription }).then((r) => r.data),

  unsubscribePush: (endpoint) =>
    ownerApi.delete('/owner/push/subscribe', { data: { endpoint } }).then((r) => r.data),

  testPush: (portalPath) =>
    ownerApi.post('/owner/push/test', { portalPath }).then((r) => r.data),
};

export default ownerAPI;
